import React, { useMemo, useState } from 'react';
import { Filter } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BlogCard } from '../components/BlogCard';
import { PageHero } from '../components/blocks/PageHero';
import { CtaBanner } from '../components/blocks/CtaBanner';
import { SURFACE } from '../components/blocks/SectionDivider';
import { blogPosts, categories, type BlogCategory } from '../data/blog';
import { useSeo } from '../lib/useSeo';
import { blogSchema } from '../lib/schema';

/**
 * PAGE: /journal
 * WORDPRESS: `archive.php` / `home.php` for the post archive
 *
 * Blocks: hero → intro_statement → category_filter → journal_grid →
 *         cta_banner
 */

const HERO_IMAGE = 'https://images.unsplash.com/photo-1500964757637-c85e8a162699?auto=format&fit=crop&q=80&w=2000';
export function Blog() {
  const [activeCategory, setActiveCategory] = useState<BlogCategory | 'all'>('all');
  const sortedPosts = useMemo(() => [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), []);
  useSeo({
    title: 'Journal — The Garden',
    description: 'Reflections on wellbeing, nature, mindfulness and everyday life — gentle companions for the moments between sessions.',
    path: '/journal',
    image: HERO_IMAGE,
    breadcrumbs: [{
      name: 'Journal',
      path: '/journal'
    }],
    schema: [blogSchema(sortedPosts.map(post => ({
      title: post.title,
      path: `/journal/${post.slug}`,
      datePublished: post.date
    })))]
  });
  const filteredPosts = useMemo(() => {
    if (activeCategory === 'all') return sortedPosts;
    return sortedPosts.filter(post => post.category === activeCategory);
  }, [activeCategory, sortedPosts]);
  const activeCategoryName = activeCategory === 'all' ? 'All reflections' : categories.find(category => category.id === activeCategory)?.name;
  const [featured, ...rest] = filteredPosts;
  return <div className="min-h-screen bg-cream" data-id="element-405" data-mp="18hdfp3cum2tr">
      <Header data-id="element-406" data-mp="14i4l5w9zi0x5" />

      <main data-id="element-407" data-mp="14id15x1elmck">
        <PageHero eyebrow="The Garden" heading="Journal" subheading="Thoughts to return to, whenever you need them." body="Sometimes the smallest ideas stay with us the longest." image={HERO_IMAGE} imageAlt="Sunlight filtering through a quiet forest" size="compact" dividerFill={SURFACE.cream} data-id="element-408" data-mp="14gtiy0w6t185" />

        <section className="bg-cream px-6 pb-24 pt-12 md:pb-28 md:pt-16" data-id="element-409" data-mp="kune234ewa9o">
          <div className="mx-auto max-w-7xl" data-id="element-410" data-mp="zi8ic0d0wfj6">
            <div className="mx-auto max-w-4xl border-b border-cream-200 pb-10 text-center md:pb-12" data-id="element-411" data-mp="zk30x80tnvk3">
              <p className="text-xs font-medium tracking-[0.26em] text-sage-700 uppercase" data-id="element-412" data-mp="uq7aqrqsvczh">
                Reflections for everyday life
              </p>
              <div className="mt-4 space-y-3 font-serif text-xl leading-snug text-charcoal sm:text-2xl" data-id="element-413" data-mp="23mizq6ewyve">
                <p data-id="element-414" data-mp="2dq7uhaaobv0i">
                  Here you&apos;ll find reflections, stories and practical
                  inspiration about wellbeing, nature, mindfulness and everyday
                  life — gentle companions for the moments between our sessions.
                </p>
                <p className="italic text-sage-700" data-id="element-415" data-mp="28rd51pi7b87f">
                  Take what speaks to you. Leave the rest for another day.
                </p>
              </div>
            </div>

            {/* BLOCK: category_filter — ACF taxonomy-driven */}
            <div className="mx-auto mt-10 max-w-3xl text-center md:mt-12" data-id="element-416" data-mp="31er0fj693vpf">
              <div className="inline-flex items-center gap-2 text-rose-700" data-id="element-417" data-mp="2nh2h4fru0wmh">
                <Filter size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-418" data-mp="2sf42568tj60l" />
                <p className="text-xs font-medium tracking-[0.26em] uppercase" data-id="element-419" data-mp="2shgodvlm02jg">
                  Browse by topic
                </p>
              </div>
              <h2 className="mt-3 font-serif text-3xl text-charcoal sm:text-4xl" data-id="element-420" data-mp="1h18k9pqj4j5o">
                Find what meets you today.
              </h2>
              <p className="mt-3 text-base font-light leading-relaxed text-charcoal-50" data-id="element-421" data-mp="1wbx2wd30xaop">
                Choose a topic below to filter the journal, or return to all
                reflections at any time.
              </p>
            </div>

            <div aria-label="Journal topics" className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3" data-id="element-422" data-mp="1ij8lffycpzah">
              {categories.map(category => {
              const count = blogPosts.filter(post => post.category === category.id).length;
              const isActive = activeCategory === category.id;
              const Icon = category.icon;
              return <button key={category.id} type="button" aria-pressed={isActive} onClick={() => setActiveCategory(isActive ? 'all' : category.id)} className={`group text-left transition-colors focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 ${isActive ? 'bg-sage p-7 text-white shadow-soft' : 'border border-cream-200 bg-white p-7 text-charcoal hover:border-sage'}`} data-id="element-423" data-mp="21b9rp3dggrtx">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-full border ${isActive ? 'border-cream/40 text-cream' : 'border-sage/50 text-sage-700'}`} data-id="element-424" data-mp="3f9jx12azl8pk">
                      <Icon size={18} strokeWidth={1.35} aria-hidden="true" data-id="element-425" data-mp="3p4v4czk2fdna" />
                    </span>
                    <span className="mt-5 block font-serif text-2xl leading-tight" data-id="element-426" data-mp="3u6ke8bvjxl9s">
                      {category.name}
                    </span>
                    <span className={`mt-2 block text-sm font-light leading-relaxed ${isActive ? 'text-cream-100/85' : 'text-charcoal-50'}`} data-id="element-427" data-mp="1zs9ps4ivct4j">
                      {category.description}
                    </span>
                    <span className={`mt-5 inline-flex border-b pb-1 text-[10px] font-medium tracking-[0.18em] uppercase ${isActive ? 'border-cream/60 text-cream' : 'border-sage/60 text-sage-700'}`} data-id="element-428" data-mp="376d8henj6s8c">
                      {isActive ? 'Showing this topic' : `View ${count} posts`}
                    </span>
                  </button>;
            })}
            </div>

            <div className="mt-7 flex flex-col items-center gap-3 text-center" data-id="element-429" data-mp="3l4bjca2n86j3">
              <button type="button" onClick={() => setActiveCategory('all')} aria-pressed={activeCategory === 'all'} className={`inline-flex items-center gap-2 border px-5 py-2.5 text-xs font-medium tracking-[0.16em] uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 ${activeCategory === 'all' ? 'border-charcoal bg-charcoal text-cream' : 'border-charcoal/25 bg-transparent text-charcoal hover:border-charcoal hover:bg-charcoal hover:text-cream'}`} data-id="element-430" data-mp="3l27gzwke7fkx">
                <Filter size={14} strokeWidth={1.5} aria-hidden="true" data-id="element-431" data-mp="w98ud7hywhw1" />
                All reflections
              </button>
              <p className="text-xs font-medium tracking-[0.14em] text-charcoal-50 uppercase" data-id="element-432" data-mp="mbufhcbp015u">
                Showing: {activeCategoryName}
              </p>
            </div>

            {/* BLOCK: journal_grid */}
            <div className="mt-16" aria-live="polite" data-id="element-433" data-mp="dbo53zbajhaw">
              {featured && <div data-id="element-434" data-mp="3urnt9jen2yj7">
                  <p className="mb-5 text-center text-xs font-medium tracking-[0.26em] text-sage-700 uppercase" data-id="element-435" data-mp="3usfgo6y31pye">
                    {activeCategory === 'all' ? 'Latest reflection' : `${activeCategoryName} · Latest`}
                  </p>
                  <BlogCard key={featured.slug} post={featured} featured data-id="element-436" data-mp="3qpsvupztx5ap" />
                </div>}

              {rest.length > 0 && <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-id="element-437" data-mp="a266h4stt2wb">
                  {rest.map(post => <BlogCard key={post.slug} post={post} data-id="element-438" data-mp="4i69zcgb36r" />)}
                </div>}

              {filteredPosts.length === 0 && <div className="py-20 text-center" data-id="element-439" data-mp="3s818q4s1fkp4">
                  <p className="font-serif text-xl italic text-charcoal-50" data-id="element-440" data-mp="3n9q0fyhe5i90">
                    No reflections in this category yet — soon.
                  </p>
                </div>}
            </div>
          </div>
        </section>

        <CtaBanner heading="Looking for a more personal experience?" buttonLabel="Start Your Journey" buttonTo="/start-your-journey" dividerFrom={SURFACE.cream} data-id="element-441" data-mp="3djo98xtqrjpv" />
      </main>

      <Footer data-id="element-442" data-mp="2zjn9xmt4kkci" />
    </div>;
}